import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../utils/cn';

const fieldBase =
  'w-full rounded-md border bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-octopus-400 focus:border-transparent disabled:opacity-50 ' +
  'dark:bg-ink-800 dark:text-sand-100 dark:placeholder:text-ink-400';

function borderClass(hasError: boolean) {
  return hasError
    ? 'border-coral-500 dark:border-coral-400'
    : 'border-sand-300 dark:border-ink-700';
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { hasError?: boolean }
>(function Input({ className, hasError = false, ...props }, ref) {
  return <input ref={ref} className={cn(fieldBase, borderClass(hasError), className)} {...props} />;
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { hasError?: boolean }
>(function Textarea({ className, hasError = false, ...props }, ref) {
  return (
    <textarea ref={ref} className={cn(fieldBase, borderClass(hasError), className)} {...props} />
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { hasError?: boolean }
>(function Select({ className, hasError = false, children, ...props }, ref) {
  return (
    <select ref={ref} className={cn(fieldBase, borderClass(hasError), className)} {...props}>
      {children}
    </select>
  );
});

export interface FormFieldProps {
  label: ReactNode;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/** Label + control + error, per MASTER_PROMPT.md §11. Pass the control (Input/Select/Textarea/
 * custom) as `children`; this component only wires up ids and the error/hint text. */
export function FormField({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  className,
}: FormFieldProps) {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {/*
        REAL BUG FOUND AND FIXED: the required-indicator used to be a real `*` text node inside
        this <label>, which became part of the label's accessible name/text content ("Slug*"
        instead of "Slug") - a screen reader read the raw character aloud, and
        `getByLabel('Slug', {exact: true})` (used by this session's own Playwright tests) could
        never match it. Tried `aria-hidden="true"` on the span first - that's the standard fix for
        the screen-reader-reads-"star"-aloud half of this, but Playwright's `getByLabel` computes
        its match from raw label text content, not the ARIA accessible-name algorithm, so an
        aria-hidden descendant is still counted and `exact: true` still failed (confirmed live).
        A CSS `::after` pseudo-element isn't a DOM text node at all, so it's invisible to BOTH
        problems at once - required-ness itself is still conveyed to assistive tech via the
        control's own `required`/`aria-required` attribute (wireControl below), so this glyph is
        purely a sighted-user visual cue either way.
      */}
      <label
        htmlFor={id}
        className={cn(
          'text-sm font-medium text-ink-800 dark:text-sand-200',
          required &&
            "after:content-['*'] after:ml-0.5 after:text-coral-600 dark:after:text-coral-400",
        )}
      >
        {label}
      </label>
      {wireControl(children, { id, describedBy, hasError: Boolean(error), required })}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-sm text-coral-600 dark:text-coral-400">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-sm text-ink-500 dark:text-ink-300">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Injects id/aria-describedby/aria-invalid/hasError into the single control child so callers
 * only ever write `<FormField label="..."><Input .../></FormField>`. Falls back to rendering the
 * child untouched if it isn't a single element (e.g. a custom composite control). */
function wireControl(
  children: ReactNode,
  opts: { id: string; describedBy?: string; hasError: boolean; required?: boolean },
): ReactNode {
  const only = Children.only(children);
  if (!isValidElement(only)) return children;
  const el = only as ReactElement<Record<string, unknown>>;
  return cloneElement(el, {
    id: el.props.id ?? opts.id,
    'aria-describedby': opts.describedBy,
    'aria-invalid': opts.hasError || undefined,
    hasError: opts.hasError,
    // REAL BUG FOUND AND FIXED alongside the label-asterisk one above: `required` only ever drove
    // the visual asterisk - never reached the actual control, so a "required" field had NO
    // semantic/accessible indication of being required at all (no HTML `required`, no
    // `aria-required`). `el.props.required` lets a caller that already sets it explicitly win.
    required: (el.props.required as boolean | undefined) ?? opts.required,
  });
}
